import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import SectionBand from '@/components/ui/SectionBand';

const FAQ_ITEMS = [
  {
    q: 'What is my Reserve balance?',
    a: "It's money you've put into Reserve—from one-time top-ups or your automatic plan. You can put it toward a reserved trip to lower what's left to pay. It doesn't replace your regular payment schedule."
  },
  {
    q: "What does “putting Reserve toward a trip” mean?",
    a: "It's just for tracking. We show how much of your balance you're applying to a trip so you see what's left to pay. It doesn't charge your card again, and you can change it or undo it anytime."
  },
  {
    q: 'Can I use my Reserve balance to pay my subscription?',
    a: "No. Your balance can't be used for your scheduled weekly or bi-weekly payments. You can only use it toward a reserved trip, which can lower the amount left on that trip (and sometimes your payment size or number of payments)."
  },
  {
    q: 'What is Reserve good for?',
    a: "Reserve works well when you want flexibility. Example: You're planning a wedding trip in six months—set up a weekly plan, let it build, and ticket when you're ready. If the fare drops before then, you pay less. Example: You're eyeing a flight but not sure about dates yet—add money to Reserve and apply it when you pick your trip. Example: You have variable income—top up when you can, and use a plan for steady progress. Reserve keeps your options open until you ticket."
  },
  {
    q: 'What happens if I cancel a trip or a plan?',
    a: "Any money that was on that trip or plan goes back into your Reserve balance. It stays there and you can use it for any other trip."
  },
  {
    q: 'How do I add money to Reserve?',
    a: "You can add funds anytime with “Add funds” (a one-time top-up from your card), or set up an automatic plan that adds a set amount on a schedule you choose."
  },
  {
    q: 'What happens when my trip is fully funded?',
    a: "Once your plan is fully paid (or you top off to expedite), you can ticket your flight. You’ll submit passenger details and we’ll use your Reserve balance to cover the trip—no new charges to your card. The balance is debited from Reserve at that time."
  },
  {
    q: 'What if the trip price went up by the time I ticket?',
    a: "Reserve prices stay live until you ticket, so fares can change. If the fare went up, you'll see the difference at checkout. You can pay the extra amount with your card to complete the booking, or switch to a different flight if you prefer."
  },
  {
    q: "What's the difference between Defera Reserve and Defera Zero?",
    a: "Defera Zero is 0% down on refundable fares—your trip price is secured at booking, protected from future airfare increases while you're in good standing, and you pay over time with no payment due until your first installment date. Cancel up to 24 hours before departure and receive 75% of payments back. Defera Reserve is different: you pay toward a trip before ticketing, and the price stays live until you ticket. If the fare goes up, you can switch flights. If it goes down, you pay less. Reserve gives you flexibility; Defera Zero gives you locked-in pricing with nothing due today."
  }
];

export default function ReserveFAQ() {
  return (
    <div className="rounded-2xl sm:rounded-3xl bg-white/[0.06] backdrop-blur-xl border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.03)_inset] overflow-hidden">
      <SectionBand
        icon={HelpCircle}
        iconColor="text-brand-300"
        accentRgb="148,163,184"
        title="Frequently Asked Questions"
      />
      <div className="p-6 sm:p-8">
        <Accordion type="single" collapsible className="w-full">
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              className="border-white/10 text-left"
            >
              <AccordionTrigger className="text-white/90 hover:text-white hover:no-underline py-4 [&[data-state=open]>svg]:text-white/70">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/60 text-sm font-normal leading-relaxed pb-4">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}
